import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useMicrosoftSSO from "../../hooks/useMicrosoftSSO";

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  padding: 24px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.92);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
  padding: 32px 40px;
  max-width: 420px;
  width: 100%;
  text-align: center;

  @media (max-width: 480px) {
    padding: 24px;
  }
`;

const Title = styled.h2`
  margin: 0 0 12px;
  color: #0c2b3f;
  font-size: 20px;
`;

const Status = styled.p`
  margin: 12px 0;
  color: #1d3e56;
  font-size: 15px;
  line-height: 1.5;
`;

const ErrorBox = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #fdecef;
  color: #8b1e2f;
  border: 1px solid #f5c0c9;
  font-size: 14px;
  line-height: 1.5;
`;

const RetryLink = styled(Link)`
  display: inline-block;
  margin-top: 20px;
  padding: 10px 16px;
  background: #195174;
  color: #ffffff;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  transition: background-color 0.15s ease;

  &:hover {
    background: #1f628d;
  }
`;

const parseQueryParams = (search) => {
  const params = new URLSearchParams(search);
  return {
    code: params.get("code"),
    state: params.get("state"),
    sessionState: params.get("session_state"),
    error: params.get("error"),
    errorDescription: params.get("error_description"),
  };
};

const MicrosoftCallbackPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const signedIn = useSelector((state) => state.auth.signedIn);

  const { code, state, error, errorDescription } = useMemo(
    () => parseQueryParams(location.search),
    [location.search]
  );

  useEffect(() => {
    if (signedIn) {
      navigate("/myinfo", { replace: true });
    }
  }, [signedIn, navigate]);

  const { status, errorMessage } = useMicrosoftSSO({
    enabled: true,
    code,
    stateParam: state,
    error,
    errorDescription,
  });

  const statusText = status || "Processing your Microsoft sign-in…";

  return (
    <Container>
      <Card>
        <Title>Microsoft Sign-In</Title>
        <Status>{statusText}</Status>
        {errorMessage && (
          <>
            <ErrorBox>{errorMessage}</ErrorBox>
            <RetryLink to="/user/login">Back to Login</RetryLink>
          </>
        )}
      </Card>
    </Container>
  );
};

export default MicrosoftCallbackPage;
